import React, { useCallback, useEffect, useMemo, useState } from "react"
import { accessFromProps } from "../lib/book-access"
import { BookShell } from "../components/book-shell"
import { PageHeader } from "../components/page-header"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { appConfirm } from "../lib/app-dialogs"
import { usePageLoading } from "../lib/page-loading"
const IMPA_MODE_KEY = "actinium-book-items-impa-mode"

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      ...(options.headers || {}),
    },
    ...options,
  })
  const contentType = res.headers.get("content-type") || ""
  const data = contentType.includes("application/json") ? await res.json() : null
  if (!res.ok) {
    const message = data?.message || data?.error || `Request failed (${res.status})`
    const details = data?.details ? JSON.stringify(data.details) : ""
    throw new Error(details ? `${message}: ${details}` : message)
  }
  return data
}

function TextInput({ label, value, onChange, type = "text", placeholder }) {
  return (
    <label className="grid gap-0.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20"
      />
    </label>
  )
}

function emptyForm() {
  return {
    product_name: "",
    item_number: "",
    unit_price: "",
    rob: "",
    impa_code: "",
    impa_description: "",
    unit: "",
  }
}

export function ItemsPage(props) {
  const {
    active_slug,
    section_title,
    user_name,
    access_role,
    can_create_company,
    can_enter_transactions,
    can_approve_documents,
    can_manage_company_settings,
    is_viewer,
  } = props

  const access = accessFromProps(props)
  const [impaMode, setImpaMode] = useState(false)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  usePageLoading(loading)
  const [error, setError] = useState("")
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState(null)
  const [query, setQuery] = useState("")

  useEffect(() => {
    try {
      const v = window.localStorage.getItem(IMPA_MODE_KEY)
      if (v === "1") setImpaMode(true)
    } catch {
      /* ignore */
    }
  }, [])

  const setImpaModePersist = useCallback((next) => {
    setImpaMode(next)
    try {
      window.localStorage.setItem(IMPA_MODE_KEY, next ? "1" : "0")
    } catch {
      /* ignore */
    }
  }, [])

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError("")
      const data = await fetchJson("/api/inventory_items")
      setRows(Array.isArray(data) ? data : [])
    } catch (e) {
      setError(e.message || "Failed to load items")
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  function setField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  function hydrate(row) {
    setEditingId(row.id)
    setForm({
      product_name: row.product_name || "",
      item_number: row.item_number || "",
      unit_price: row.unit_price != null ? String(row.unit_price) : "",
      rob: row.rob != null ? String(row.rob) : "",
      impa_code: row.impa_code || "",
      impa_description: row.impa_description || "",
      unit: row.unit || "",
    })
    document.getElementById("book-primary-form")?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  function resetForm() {
    setEditingId(null)
    setForm(emptyForm())
  }

  async function onSubmit(e) {
    e.preventDefault()
    setError("")
    const body = {
      inventory_item: {
        product_name: form.product_name,
        item_number: form.item_number,
        unit_price: form.unit_price,
        rob: form.rob,
        impa_code: form.impa_code,
        impa_description: form.impa_description,
        unit: form.unit,
      },
    }
    try {
      if (editingId) {
        await fetchJson(`/api/inventory_items/${editingId}`, { method: "PATCH", body: JSON.stringify(body) })
      } else {
        await fetchJson("/api/inventory_items", { method: "POST", body: JSON.stringify(body) })
      }
      resetForm()
      await load()
    } catch (err) {
      setError(err.message || "Save failed")
    }
  }

  async function onDelete(id) {
    if (!(await appConfirm("Delete this item?", { title: "Delete item", danger: true, confirmLabel: "Delete" }))) return
    try {
      await fetchJson(`/api/inventory_items/${id}`, { method: "DELETE" })
      if (editingId === id) resetForm()
      await load()
    } catch (e) {
      setError(e.message || "Delete failed")
    }
  }

  const filtered = useMemo(() => {
    if (!query.trim()) return rows
    const q = query.trim().toLowerCase()
    return rows.filter((r) =>
      [r.product_name, r.item_number, r.impa_code, r.impa_description, r.unit, r.unit_price, r.rob]
        .map((x) => String(x ?? "").toLowerCase())
        .some((s) => s.includes(q)),
    )
  }, [rows, query])

  return (
    <BookShell activeSlug={active_slug || "items"} userName={user_name} access={access}>
      <div className="flex flex-col gap-6">
        <PageHeader
          context="Items"
          title={section_title || "Item master"}
          description="Product catalog in standard layout, or IMPA (marine stores) codes with units — aligned with Tally-style inventory masters."
          actions={
            <label className="flex cursor-pointer items-center gap-2 text-[14px] font-medium text-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border"
                checked={impaMode}
                onChange={(e) => setImpaModePersist(e.target.checked)}
              />
              IMPA mode
            </label>
          }
        />

        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/25 px-3 py-2 text-[13px] text-muted-foreground">
          <span>
            <strong className="text-foreground">Tally-style ERP in this app:</strong> use{" "}
            <strong className="text-foreground">Accountant</strong> for journals, trial balance, chart of accounts, and India GST (
            <a className="text-primary underline" href="/accountant/gstr1-sales">
              GSTR-1
            </a>
            ,{" "}
            <a className="text-primary underline" href="/accountant/gstr2-purchases">
              GSTR-2 / ITC
            </a>
            ); <strong className="text-foreground">Sales / Purchases</strong> for GST line items on invoices and bills;{" "}
            <strong className="text-foreground">Items</strong> here for stock masters (standard or IMPA).
          </span>
        </div>

        {error ? <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div> : null}

        <Card id="book-primary-form">
          <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
            <CardTitle>{editingId ? "Edit item" : impaMode ? "New IMPA item" : "New product"}</CardTitle>
            {editingId ? (
              <Button type="button" variant="ghost" onClick={resetForm}>
                Cancel edit
              </Button>
            ) : null}
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
              {!impaMode ? (
                <>
                  <TextInput label="Name of product" value={form.product_name} onChange={(v) => setField("product_name", v)} />
                  <TextInput label="Item number" value={form.item_number} onChange={(v) => setField("item_number", v)} />
                  <TextInput label="Unit price" value={form.unit_price} onChange={(v) => setField("unit_price", v)} placeholder="0.00" />
                  <TextInput label="Current ROB" value={form.rob} onChange={(v) => setField("rob", v)} placeholder="Receipt / balance qty" />
                </>
              ) : (
                <>
                  <TextInput label="IMPA code" value={form.impa_code} onChange={(v) => setField("impa_code", v)} />
                  <TextInput label="Unit" value={form.unit} onChange={(v) => setField("unit", v)} placeholder="PCS, MTR, KG…" />
                  <label className="grid gap-0.5 text-xs md:col-span-2">
                    <span className="text-muted-foreground">Description</span>
                    <textarea
                      value={form.impa_description}
                      onChange={(e) => setField("impa_description", e.target.value)}
                      rows={2}
                      className="w-full rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20"
                      placeholder="IMPA description"
                    />
                  </label>
                  <TextInput label="Unit price" value={form.unit_price} onChange={(v) => setField("unit_price", v)} placeholder="0.00" />
                  <TextInput label="ROB" value={form.rob} onChange={(v) => setField("rob", v)} placeholder="Receipt / balance qty" />
                </>
              )}
              <div className="md:col-span-2 flex flex-wrap gap-2">
                <Button type="submit">{editingId ? "Save" : "Create"}</Button>
                <Button type="button" variant="ghost" onClick={() => load()}>
                  Refresh
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
            <CardTitle>{impaMode ? "IMPA catalog" : "Product list"}</CardTitle>
            <div className="text-xs text-muted-foreground">{loading ? "Loading…" : `${filtered.length} record(s)`}</div>
          </CardHeader>
          <CardContent>
            <div className="mb-2 max-w-sm">
              <TextInput label="Search" value={query} onChange={setQuery} placeholder="Filter…" />
            </div>
            <div className="overflow-x-auto rounded-xl border border-border/90 bg-card shadow-soft">
              <table className="min-w-full border-collapse text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/35 text-left text-muted-foreground">
                    {!impaMode ? (
                      <>
                        <th className="px-2 py-1.5 font-medium">Name of product</th>
                        <th className="px-2 py-1.5 font-medium">Item number</th>
                        <th className="px-2 py-1.5 font-medium">Unit price</th>
                        <th className="px-2 py-1.5 font-medium">Current ROB</th>
                      </>
                    ) : (
                      <>
                        <th className="px-2 py-1.5 font-medium">IMPA code</th>
                        <th className="px-2 py-1.5 font-medium">Description</th>
                        <th className="px-2 py-1.5 font-medium">Unit</th>
                        <th className="px-2 py-1.5 font-medium">Unit price</th>
                        <th className="px-2 py-1.5 font-medium">ROB</th>
                      </>
                    )}
                    <th className="px-2 py-1.5 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr key={row.id} className="border-b border-border/60 bg-card last:border-0 hover:bg-muted/20">
                      {!impaMode ? (
                        <>
                          <td className="px-2 py-1.5 text-foreground/90">{row.product_name || "—"}</td>
                          <td className="px-2 py-1.5 text-foreground/90">{row.item_number || "—"}</td>
                          <td className="px-2 py-1.5 tabular-nums">{row.unit_price}</td>
                          <td className="px-2 py-1.5 tabular-nums">{row.rob}</td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-1.5 font-mono text-[12px] text-foreground/90">{row.impa_code || "—"}</td>
                          <td className="max-w-[220px] truncate px-2 py-1.5 text-foreground/90" title={row.impa_description || ""}>
                            {row.impa_description || "—"}
                          </td>
                          <td className="px-2 py-1.5">{row.unit || "—"}</td>
                          <td className="px-2 py-1.5 tabular-nums">{row.unit_price}</td>
                          <td className="px-2 py-1.5 tabular-nums">{row.rob}</td>
                        </>
                      )}
                      <td className="px-2 py-1.5">
                        <div className="flex flex-wrap gap-1">
                          <Button type="button" variant="ghost" className="h-7 px-1.5 text-xs" onClick={() => hydrate(row)}>
                            Edit
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            className="h-7 px-1.5 text-xs text-red-600 hover:text-red-700"
                            onClick={() => onDelete(row.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && filtered.length === 0 ? (
                    <tr>
                      <td className="px-2 py-3 text-muted-foreground" colSpan={impaMode ? 6 : 5}>
                        No items yet. Add one above.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </BookShell>
  )
}
